import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { environment } from 'environments/environment';
import { Observable, throwError } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { selectUser } from '../store/selectors/userData.selector';
import { IAppState } from '../store/state/app.state';

// used to invalidate old encrypted links without server
export function isShareToken(token: string): boolean {
  // check for 2 part token split by (.)
  const parts = token?.split('.');
  return parts?.length === 2 && parts.every(part => part.length > 0);
}

export interface ReportRoutePayload {
  reportId: string;
  oktaId: string;
}

//for Base64URL (RFC 4648) standard encoding
function encodeBase64Url(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

//for Base64URL (RFC 4648) standard decoding
function decodeBase64Url(value: string): string {
  const base64 = value.replace(/-/g, '+').replace(/_/g, '/');
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  return atob(base64 + padding);
}

export function encodeReportRoutePayload(payload: ReportRoutePayload): string {
  return encodeBase64Url(JSON.stringify(payload));
}

export function decodeReportRoutePayload(encodedPayload: string): ReportRoutePayload {
  return JSON.parse(decodeBase64Url(encodedPayload)) as ReportRoutePayload;
}

@Injectable()
export class ShareTokenService {
  private headers = new HttpHeaders();

  constructor(private http: HttpClient, private store: Store<IAppState>) {}

  createRoutePayloadForCurrentUser(reportId: string | number): Observable<string> {
    return this.store.select(selectUser).pipe(
      take(1),
      map(user => encodeReportRoutePayload({ reportId: reportId.toString(), oktaId: user.oktaId }))
    );
  }

  createToken(reportId: string | number, oktaId: string): Observable<string> {
    return this.http
      .get<{ token: string }>(
        `${environment.apiBaseUrl}/api/share/token?reportId=${encodeURIComponent(reportId)}&oktaId=${encodeURIComponent(oktaId)}`,
        { headers: this.headers }
      )
      .pipe(
        map(response => response.token),
        catchError(this.handleError)
      );
  }

  private handleError(error: HttpErrorResponse) {
    return throwError(() => new Error(error.message));
  }
}